'use client'
import { PrismicImage, PrismicRichText, PrismicTable } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import CtaLight from "@/components/CtaLight";
import { sumupTrigger } from "@/components/SideMenu/SideMenu";

/**
 * @typedef {import("@prismicio/client").Content.ArticleContentSlice} ArticleContentSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<ArticleContentSlice>} ArticleContentProps
 * @type {import("react").FC<ArticleContentProps>}
 */


const ArticleContent = ({ slice }) => {
  const { text, img, img_mobile, legend, table_article, color, cta, video } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white text-dark-green"
    >
      {slice.variation === 'default' && isFilled.richText(text) && (
        <div className="flex flex-col gap-2 mb-6 framing">
          <PrismicRichText
            field={text}
            components={{
              heading2: ({ children, text }) => <h2 className="h2-blog sumup-trigger mb-2" id={sumupTrigger(text)}>{children}</h2>,
              heading3: ({ children }) => <h3 className="h3-blog">{children}</h3>,
              heading4: ({ children }) => <h4 className="h4-barlow mb-2 mt-6">{children}</h4>,
              heading5: ({ children }) => <h5 className="h5-barlow mb-2 mt-6">{children}</h5>,
              paragraph: ({ children }) => <p className="mb-2 [&:has(a_.bouton)]:py-6">{children}</p>,
              strong: ({ children }) => (
                <strong className="font-semibold">{children}</strong>
              ),
              oList: ({ children }) => (
                <ol className="list-decimal pl-4 md:pl-5">{children}</ol>
              ),
              list: ({ children }) => (
                <ul className="ul-yellow pl-3"> {children} </ul>
              ),
            }}
          />
        </div>
      )}

      {slice.variation === 'image' &&
        <figure className="flex flex-col gap-2 my-12 framing">
          {isFilled.image(img) && (
            <div className="hidden md:block">
              <PrismicImage
                field={img}
                className="w-full h-full max-h-140 object-cover"
              />
            </div>
          )}
          {isFilled.image(img_mobile) && (
            <div className="md:hidden">
              <PrismicImage
                field={img_mobile}
                className="w-full h-full max-h-140 object-cover"
              />
            </div>
          )}
          {isFilled.richText(legend) &&
            <figcaption>
              <PrismicRichText
                field={legend}
                components={{
                  paragraph: ({ children }) => <p className="p-sm text-center ">{children}</p>,
                  strong: ({ children }) => (
                    <strong className="font-semibold">{children}</strong>
                  ),
                }}
              />
            </figcaption>
          }
        </figure>
      }

      {slice.variation === 'video' &&
        isFilled.embed(video) && (
          <div className="flex flex-col gap-2 my-12 framing content-video">
            <div
              className="aspect-video w-full"
              aria-label={video.title || "Vidéo de l'article"}
              title={video.title || "Vidéo de l'article"}
              dangerouslySetInnerHTML={{ __html: video.embed_url ? video.html : "" }}
            />
            {video.title && (
              <p className="sr-only">{video.title}</p>
            )}
          </div>
        )
      }

      {slice.variation === "table" &&
          <div
          className="w-full my-12 overflow-x-auto no-scrollbar flex pr-5 before:content-[''] before:block before:flex-none before:w-5 md:before:w-0 md:px-10 lg:px-23"
        >
          <PrismicTable
            field={table_article}
            components={{
              table: ({ children }) => (
                <table className="table-auto mb-3.5 lg:mb-0 w-full">
                  {children}
                </table>
              ),
              thead: ({ children }) => <thead className="">{children}</thead>,
              tbody: ({ children }) => <tbody>{children}</tbody>,
              th: ({ children }) => (
                <th className="px-3 py-2 text-left font-semibold border-1 whitespace-nowrap">
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td className="px-3 py-2 align-top border-1 min-w-40 md:min-w-none">
                  {children}
                </td>
              ),
            }}
          />
        </div>
      }

      {slice.variation === "cta" &&
        <div className="framing my-12">
          {isFilled.richText(text) &&
            <div className={`bg-${color} px-8 pt-8 pb-12 md:p-10 md:pb-14 flex flex-col gap-7.5 text-white relative`}>
              <div className="absolute top-0 right-0 md:right-10 h-full z-[0]">
                <img src="/y-blog.svg" alt="décoration" className="h-full w-full object-cover relative z-[0]" />
              </div>
              <div className="flex flex-col gap-2.5 md:max-w-[85%] relative z-[10]">
                <PrismicRichText
                  field={text}
                  components={{
                    heading2: ({ children, text }) => <h2 className="title-cta" id={sumupTrigger(text)}>{children}</h2>,
                    heading3: ({ children }) => <h3 className="h3-blog">{children}</h3>,
                    paragraph: ({ children }) => <p className="p">{children}</p>,
                  }}
                />
              </div>
              {isFilled.link(cta) &&
                <div className="relative z-[10]">
                  <CtaLight page={cta} padding={"px-5"} width={"w-fit"} />
                </div>
              }
            </div>
          }
        </div>
      }

    </section >
  );
};

export default ArticleContent;
